import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Button,
  Image,
  FormControl,
  FormLabel,
  Textarea,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState, useContext } from "react";
import { AppContext } from "contexts/AppContext";
import InputField from "views/admin/events/components/InputField";
import CategoryMultiSelect from "components/CategoryMultiSelect";
import { addMenuCategory } from "Api/apiService";
import FileUpload from "views/admin/events/components/FileUpload";

const normalizeCategory = (value) => String(value || "").trim();
const toKey = (value) => normalizeCategory(value).toLowerCase();

// Styled Components

export default function MenuItemModal({
  isOpen,
  onClose,
  selectedMenuItem,
  updateMenuItem,
  onDeleteMenuItem,
}) {
  const [itemName, setItemName] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [itemPrice, setItemPrice] = useState(null);

  const [itemNameError, setItemNameError] = useState("");
  const [itemDescriptionError, setItemDescriptionError] = useState("");
  const [itemPriceError, setItemPriceError] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState("");
  const [uploadError, setUploadError] = useState(null);
  const [fileData, setFileData] = useState(null);

  const textColor = useColorModeValue("navy.700", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");

  const { menuCategories, fetchMenuCategories } = useContext(AppContext);

  const categoryOptions = useMemo(
    () => (menuCategories || []).map((category) => category.name),
    [menuCategories]
  );

  const handleItemNameChange = (e) => {
    setItemName(e.target.value);
  };

  const handleItemDescriptionChange = (e) => {
    setItemDescription(e.target.value);
  };
  const handleItemPriceChange = (e) => {
    setItemPrice(e.target.value);
  };

  useEffect(() => {
    if (isOpen) {
      // Load data when modal opens
      setItemPrice(selectedMenuItem?.itemPrice);
      setItemName(selectedMenuItem?.itemName);
      setItemDescription(selectedMenuItem?.itemDescription);
      setSelectedCategories(selectedMenuItem?.itemCategory || []);
      setCategoryInput("");
      fetchMenuCategories();
    } else {
      // Reset data and errors when modal closes
      setItemPrice(null);
      setItemName("");
      setItemDescription("");
      setSelectedCategories([]);
      setCategoryInput("");

      setItemNameError("");
      setItemPriceError("");
      setItemDescriptionError("");
      setUploadError(null);
      setFileData(null);
    }
  }, [isOpen, selectedMenuItem]);

  const handleFileSelect = (file) => {
    setFileData(file);
  };

  const handleFileRemove = () => {
    setFileData(null);
  };

  const resolveCategories = async () => {
    const trimmedInput = normalizeCategory(categoryInput);
    let nextSelected = selectedCategories;

    if (trimmedInput) {
      const inputKey = toKey(trimmedInput);
      const hasSelected = selectedCategories.some(
        (category) => toKey(category) === inputKey
      );
      if (!hasSelected) {
        nextSelected = [...selectedCategories, trimmedInput];
      }

      const hasOption = categoryOptions.some(
        (category) => toKey(category) === inputKey
      );
      if (!hasOption) {
        try {
          await addMenuCategory({ type: "menu", name: trimmedInput });
          fetchMenuCategories();
        } catch (error) {
          console.error("Error adding menu category:", error);
        }
      }
    }

    return nextSelected;
  };

  const handleSave = async () => {
    console.log("itemPrice", itemPrice);
    try {
      let isValid = true;

      if (!itemName) {
        setItemNameError("Name is required.");
        isValid = false;
      } else {
        setItemNameError("");
      }

      if (!itemDescription) {
        setItemDescriptionError("Description is required.");
        isValid = false;
      } else {
        setItemDescriptionError("");
      }

      if (!itemPrice) {
        console.log("here ????");
        setItemPriceError("Price is required.");
        isValid = false;
      } else {
        setItemPriceError("");
      }

      if (!isValid) {
        return;
      }

      const resolvedCategories = await resolveCategories();
      setSelectedCategories(resolvedCategories);
      setCategoryInput("");

      updateMenuItem(
        selectedMenuItem._id,
        itemName,
        resolvedCategories,
        itemDescription,
        itemPrice,
        fileData
      );

      onClose();
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  const handleDelete = async () => {
    if (!selectedMenuItem?._id) {
      return;
    }
    try {
      await onDeleteMenuItem(selectedMenuItem._id);
      onClose();
    } catch (error) {
      console.error("Error deleting menu item:", error);
    }
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader flex={1} textAlign="center">
          Menu Item Details
        </ModalHeader>

        <ModalCloseButton />
        <ModalBody>
          <InputField
            label="Menu Item Name"
            type="text"
            value={itemName}
            onChange={handleItemNameChange}
            errorMessage={itemNameError}
            isRequired={true}
          />

          <FormControl isInvalid={!!itemDescriptionError}>
            <FormLabel
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
            >
              <Flex align="center">
                Item Description<Text color={brandStars}>*</Text>
              </Flex>
            </FormLabel>
            <Textarea
              isInvalid={itemDescriptionError ? true : false}
              isRequired={true}
              fontSize="sm"
              size="lg"
              value={itemDescription}
              onChange={handleItemDescriptionChange}
              mb={itemDescriptionError ? "5px" : "24px"}
            />
            {itemDescriptionError && (
              <Text
                mb="14px"
                color="red.500"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
              >
                {itemDescriptionError}
              </Text>
            )}
          </FormControl>
          <InputField
            label="Menu Item Price"
            type="number"
            value={itemPrice}
            onChange={handleItemPriceChange}
            errorMessage={itemPriceError}
            isRequired={true}
          />

          <CategoryMultiSelect
            label="Item Category"
            options={categoryOptions}
            selected={selectedCategories}
            onSelectedChange={setSelectedCategories}
            inputValue={categoryInput}
            onInputChange={setCategoryInput}
          />
          <Text fontSize="sm" fontWeight="700" mb="8px">
            Menu Item Image
          </Text>
          {selectedMenuItem?.itemImage && !fileData ? (
            <>
              <Text fontSize="xs" color="gray.500" mb="8px">
                Current image
              </Text>
              <Image
                src={
                  String(selectedMenuItem.itemImage).startsWith("http")
                    ? selectedMenuItem.itemImage
                    : `https://thegrovebing.com/prod/${String(
                        selectedMenuItem.itemImage
                      ).replace(/^\/+/, "")}`
                }
                alt={selectedMenuItem?.itemName || "menu item"}
                maxH="220px"
                w="100%"
                objectFit="cover"
                borderRadius="12px"
                mb="12px"
              />
            </>
          ) : null}
          <FileUpload
            error={uploadError}
            setError={setUploadError}
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
          />

          <Button
            fontSize="sm"
            variant="brand"
            fontWeight="500"
            w="100%"
            h="50"
            mb="24px"
            onClick={handleSave}
          >
            Save
          </Button>
          <Button
            fontSize="sm"
            variant="outline"
            colorScheme="red"
            w="100%"
            h="50"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
